/**
 * Notional Consolidation View (SPRINT-25 F007)
 *
 * Enhanced multi-entity consolidation waterfall:
 * Entity EBITDA → Intercompany Eliminations → Guarantor Income → Deal Adjustments → Adjusted EBITDA
 *
 * Multi-period columns (FY2023, FY2024, etc.)
 * Entity drill-down: click entity name → navigate to account detail
 * Guarantor drill-down: click name → navigate to contact detail
 *
 * Replaces/enhances the ConsolidatedIncomeTable component in the Consolidated tab.
 *
 * Props: { dealId: string }
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { BarChart3 } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnalysisEntity {
  id: string;
  dealId: string;
  accountId: string;
  accountName?: string;
  role: string;
  entityRole?: string;
  displayOrder?: number;
}

interface ConsolidatedSnapshot {
  id: string;
  dealId: string;
  periodLabel: string;
  totalEntityEbitda: number;
  totalEliminations: number;
  totalGuarantorIncome: number;
  totalAdjustments: number;
  adjustedEbitda: number;
  entityBreakdown?: Array<{
    accountId: string;
    accountName: string;
    ebitda: number;
    isAnnualised: boolean;
  }>;
}

interface EliminationEntry {
  id: string;
  dealId: string;
  fromAccountId: string;
  toAccountId: string;
  flowType: string;
  description: string;
  annualAmount: number;
  periodLabel: string;
  notes: string | null;
}

interface AnalysisGuarantor {
  id: string;
  dealId: string;
  contactId: string;
  contactName?: string;
  treatmentType: string;
  annualIncome?: number;
  contributionPercentage?: number;
  displayOrder?: number;
}

interface EbitdaAdjustment {
  id: string;
  dealId: string;
  adjustmentType: string;
  description: string;
  periodLabel: string;
  amount: string | number;
}

export interface NotionalConsolidationViewProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatAmount(value: number, isNegative = false): string {
  const formatted = formatCurrency(Math.abs(value));
  return isNegative ? `(${formatted})` : formatted;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  label: string;
  colSpan: number;
  "data-testid": string;
}

function SectionHeader({ label, colSpan, "data-testid": testId }: SectionHeaderProps) {
  return (
    <tr data-testid={testId}>
      <td
        colSpan={colSpan}
        style={{ backgroundColor: "#334759" }}
        className="text-white font-semibold text-xs uppercase tracking-wider py-2 px-3"
      >
        {label}
      </td>
    </tr>
  );
}

interface TotalRowProps {
  label: string;
  periods: string[];
  valueByPeriod: Record<string, number>;
  isNegative?: boolean;
  "data-testid"?: string;
}

function TotalRow({ label, periods, valueByPeriod, isNegative = false, "data-testid": testId }: TotalRowProps) {
  return (
    <tr
      data-testid={testId}
      className="font-semibold border-t-2 border-b"
    >
      <td className="py-2 px-3 text-sm">{label}</td>
      {periods.map((p) => {
        const val = valueByPeriod[p] ?? 0;
        return (
          <td key={p} className={`py-2 px-3 text-sm text-right ${isNegative && val > 0 ? "text-red-600" : ""}`}>
            {isNegative && val > 0 ? formatAmount(val, true) : formatCurrency(val)}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function NotionalConsolidationView({ dealId }: NotionalConsolidationViewProps) {
  // Fetch all data sources
  const { data: entities = [], isLoading: loadingEntities } = useQuery<AnalysisEntity[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-entities`],
    enabled: !!dealId,
  });

  const { data: snapshots = [], isLoading: loadingSnapshots } = useQuery<ConsolidatedSnapshot[]>({
    queryKey: [`/api/financial/deals/${dealId}/consolidated-snapshots`],
    enabled: !!dealId,
  });

  const { data: eliminations = [], isLoading: loadingEliminations } = useQuery<EliminationEntry[]>({
    queryKey: [`/api/financial/deals/${dealId}/intercompany-eliminations`],
    enabled: !!dealId,
  });

  const { data: guarantors = [], isLoading: loadingGuarantors } = useQuery<AnalysisGuarantor[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`],
    enabled: !!dealId,
  });

  const { data: adjustments = [], isLoading: loadingAdjustments } = useQuery<EbitdaAdjustment[]>({
    queryKey: [`/api/deal-analysis/deals/${dealId}/ebitda-adjustments`],
    enabled: !!dealId,
  });

  const isLoading = loadingEntities || loadingSnapshots || loadingEliminations || loadingGuarantors || loadingAdjustments;

  // Derive sorted period labels from snapshots
  const periods = useMemo<string[]>(() => {
    const labels = Array.from(new Set(snapshots.map((s) => s.periodLabel)));
    return labels.sort();
  }, [snapshots]);

  // Build lookup: periodLabel -> snapshot
  const snapshotByPeriod = useMemo<Record<string, ConsolidatedSnapshot>>(() => {
    const map: Record<string, ConsolidatedSnapshot> = {};
    for (const s of snapshots) {
      map[s.periodLabel] = s;
    }
    return map;
  }, [snapshots]);

  // Build entity EBITDA by accountId and period
  const entityEbitdaByPeriod = useMemo<Record<string, Record<string, number>>>(() => {
    const result: Record<string, Record<string, number>> = {};
    for (const snap of snapshots) {
      if (!snap.entityBreakdown) continue;
      for (const eb of snap.entityBreakdown) {
        if (!result[eb.accountId]) result[eb.accountId] = {};
        result[eb.accountId][snap.periodLabel] = eb.ebitda;
      }
    }
    return result;
  }, [snapshots]);

  // Build entity isAnnualised by accountId and period
  const entityAnnualisedByPeriod = useMemo<Record<string, Record<string, boolean>>>(() => {
    const result: Record<string, Record<string, boolean>> = {};
    for (const snap of snapshots) {
      if (!snap.entityBreakdown) continue;
      for (const eb of snap.entityBreakdown) {
        if (!result[eb.accountId]) result[eb.accountId] = {};
        result[eb.accountId][snap.periodLabel] = eb.isAnnualised;
      }
    }
    return result;
  }, [snapshots]);

  // Build elimination total by period
  const eliminationTotalByPeriod = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    for (const p of periods) {
      result[p] = snapshotByPeriod[p]?.totalEliminations ?? 0;
    }
    return result;
  }, [periods, snapshotByPeriod]);

  // Build eliminations grouped by period
  const eliminationsByPeriod = useMemo<Record<string, EliminationEntry[]>>(() => {
    const result: Record<string, EliminationEntry[]> = {};
    for (const elim of eliminations) {
      if (!result[elim.periodLabel]) result[elim.periodLabel] = [];
      result[elim.periodLabel].push(elim);
    }
    return result;
  }, [eliminations]);

  // Build guarantor income by contactId and period
  const guarantorIncomeByPeriod = useMemo<Record<string, Record<string, number>>>(() => {
    const result: Record<string, Record<string, number>> = {};
    for (const p of periods) {
      const snap = snapshotByPeriod[p];
      if (!snap) continue;
      // Distribute total guarantor income equally across guarantors if no breakdown
      const perGuarantor = guarantors.length > 0
        ? (snap.totalGuarantorIncome ?? 0) / guarantors.length
        : 0;
      for (const g of guarantors) {
        if (!result[g.contactId]) result[g.contactId] = {};
        result[g.contactId][p] = perGuarantor;
      }
    }
    return result;
  }, [periods, snapshotByPeriod, guarantors]);

  // Build guarantor income total by period
  const guarantorTotalByPeriod = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    for (const p of periods) {
      result[p] = snapshotByPeriod[p]?.totalGuarantorIncome ?? 0;
    }
    return result;
  }, [periods, snapshotByPeriod]);

  // Build adjustments by id and period
  const adjustmentsByPeriod = useMemo<Record<string, EbitdaAdjustment[]>>(() => {
    const result: Record<string, EbitdaAdjustment[]> = {};
    for (const adj of adjustments) {
      if (!result[adj.periodLabel]) result[adj.periodLabel] = [];
      result[adj.periodLabel].push(adj);
    }
    return result;
  }, [adjustments]);

  // Build adjustments total by period
  const adjustmentTotalByPeriod = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    for (const p of periods) {
      result[p] = snapshotByPeriod[p]?.totalAdjustments ?? 0;
    }
    return result;
  }, [periods, snapshotByPeriod]);

  // Build adjusted EBITDA by period
  const adjustedEbitdaByPeriod = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    for (const p of periods) {
      result[p] = snapshotByPeriod[p]?.adjustedEbitda ?? 0;
    }
    return result;
  }, [periods, snapshotByPeriod]);

  // Build entity total EBITDA by period
  const entityTotalByPeriod = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    for (const p of periods) {
      result[p] = snapshotByPeriod[p]?.totalEntityEbitda ?? 0;
    }
    return result;
  }, [periods, snapshotByPeriod]);

  // Unique elimination IDs across all periods for rendering (deduplicated)
  const uniqueEliminations = useMemo<EliminationEntry[]>(() => {
    const seen = new Set<string>();
    const result: EliminationEntry[] = [];
    for (const elim of eliminations) {
      if (!seen.has(elim.id)) {
        seen.add(elim.id);
        result.push(elim);
      }
    }
    return result;
  }, [eliminations]);

  // Unique adjustments across all periods (by id)
  const uniqueAdjustments = useMemo<EbitdaAdjustment[]>(() => {
    const seen = new Set<string>();
    const result: EbitdaAdjustment[] = [];
    for (const adj of adjustments) {
      if (!seen.has(adj.id)) {
        seen.add(adj.id);
        result.push(adj);
      }
    }
    return result;
  }, [adjustments]);

  const colSpan = periods.length + 1;

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  if (isLoading) {
    return (
      <Card data-testid="notional-consolidation-view">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Empty state (no entities configured)
  // -----------------------------------------------------------------------
  if (entities.length === 0) {
    return (
      <Card data-testid="notional-consolidation-view">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Notional Consolidation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            data-testid="notional-consolidation-empty"
            className="flex flex-col items-center justify-center py-12 text-center"
          >
            <BarChart3 className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              Configure entities in the Configuration tab to begin consolidation
            </p>
            <p className="text-xs text-muted-foreground">
              Add entities and financial periods to see the consolidation waterfall
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Loaded state — waterfall table
  // -----------------------------------------------------------------------
  const sortedEntities = [...entities].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  const sortedGuarantors = [...guarantors].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return (
    <Card data-testid="notional-consolidation-view">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Notional Consolidation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground w-[45%]">
                  Item
                </th>
                {periods.map((p) => (
                  <th
                    key={p}
                    data-testid={`period-col-${p}`}
                    className="text-right py-2 px-3 font-medium text-muted-foreground min-w-[120px]"
                  >
                    {p}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>

              {/* ============================================================ */}
              {/* SECTION 1: ENTITY EBITDA */}
              {/* ============================================================ */}
              <SectionHeader
                label="Entity EBITDA"
                colSpan={colSpan}
                data-testid="section-entity-ebitda"
              />

              {sortedEntities.map((entity) => {
                const periodValues = entityEbitdaByPeriod[entity.accountId] ?? {};
                const annualisedValues = entityAnnualisedByPeriod[entity.accountId] ?? {};
                return (
                  <tr
                    key={entity.accountId}
                    data-testid={`entity-row-${entity.accountId}`}
                    className="border-b hover:bg-muted/30"
                  >
                    <td className="py-2 px-3 text-sm">
                      <Link
                        href={`/accounts/${entity.accountId}`}
                        className="text-blue-600 hover:underline flex items-center gap-1.5"
                      >
                        {entity.accountName ?? entity.accountId}
                        {Object.values(annualisedValues).some(Boolean) && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1">
                            ann.
                          </Badge>
                        )}
                      </Link>
                    </td>
                    {periods.map((p) => (
                      <td key={p} className="py-2 px-3 text-sm text-right">
                        {periodValues[p] !== undefined
                          ? formatCurrency(periodValues[p])
                          : "\u2014"}
                      </td>
                    ))}
                  </tr>
                );
              })}

              <TotalRow
                label="Total Entity EBITDA"
                periods={periods}
                valueByPeriod={entityTotalByPeriod}
                data-testid="row-total-entity-ebitda"
              />

              {/* ============================================================ */}
              {/* SECTION 2: INTERCOMPANY ELIMINATIONS */}
              {/* ============================================================ */}
              {entities.length > 1 && (
                <>
                  <SectionHeader
                    label="Intercompany Eliminations"
                    colSpan={colSpan}
                    data-testid="section-eliminations"
                  />

                  {uniqueEliminations.length > 0 ? (
                    uniqueEliminations.map((elim) => {
                      return (
                        <tr
                          key={elim.id}
                          data-testid={`elimination-row-${elim.id}`}
                          className="border-b hover:bg-muted/30"
                        >
                          <td className="py-2 px-3 text-sm text-red-700">
                            {elim.description}
                          </td>
                          {periods.map((p) => {
                            const elimsForPeriod = eliminationsByPeriod[p] ?? [];
                            const match = elimsForPeriod.find((e) => e.id === elim.id);
                            return (
                              <td key={p} className="py-2 px-3 text-sm text-right text-red-600">
                                {match !== undefined
                                  ? formatAmount(match.annualAmount, true)
                                  : "\u2014"}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="border-b">
                      <td
                        colSpan={colSpan}
                        className="py-3 px-3 text-xs text-muted-foreground text-center"
                      >
                        No intercompany eliminations configured
                      </td>
                    </tr>
                  )}

                  <TotalRow
                    label="Total Eliminations"
                    periods={periods}
                    valueByPeriod={eliminationTotalByPeriod}
                    isNegative
                    data-testid="row-total-eliminations"
                  />
                </>
              )}

              {/* ============================================================ */}
              {/* SECTION 3: GUARANTOR INCOME */}
              {/* ============================================================ */}
              <SectionHeader
                label="Guarantor Income"
                colSpan={colSpan}
                data-testid="section-guarantor-income"
              />

              {sortedGuarantors.length > 0 ? (
                sortedGuarantors.map((guarantor) => {
                  const incomeByP = guarantorIncomeByPeriod[guarantor.contactId] ?? {};
                  return (
                    <tr
                      key={guarantor.contactId}
                      data-testid={`guarantor-row-${guarantor.contactId}`}
                      className="border-b hover:bg-muted/30"
                    >
                      <td className="py-2 px-3 text-sm">
                        <Link
                          href={`/contacts/${guarantor.contactId}`}
                          className="text-blue-600 hover:underline"
                        >
                          {guarantor.contactName ?? guarantor.contactId}
                        </Link>
                      </td>
                      {periods.map((p) => (
                        <td key={p} className="py-2 px-3 text-sm text-right">
                          {incomeByP[p] !== undefined
                            ? formatCurrency(incomeByP[p])
                            : "\u2014"}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr className="border-b">
                  <td
                    colSpan={colSpan}
                    className="py-3 px-3 text-xs text-muted-foreground text-center"
                  >
                    No guarantor income configured
                  </td>
                </tr>
              )}

              {sortedGuarantors.length > 0 && (
                <TotalRow
                  label="Total Guarantor Income"
                  periods={periods}
                  valueByPeriod={guarantorTotalByPeriod}
                  data-testid="row-total-guarantor-income"
                />
              )}

              {/* ============================================================ */}
              {/* SECTION 4: DEAL EBITDA ADJUSTMENTS */}
              {/* ============================================================ */}
              <SectionHeader
                label="Deal EBITDA Adjustments"
                colSpan={colSpan}
                data-testid="section-adjustments"
              />

              {uniqueAdjustments.length > 0 ? (
                uniqueAdjustments.map((adj) => {
                  const isAddBack = adj.adjustmentType === "add_back";
                  return (
                    <tr
                      key={adj.id}
                      data-testid={`adjustment-row-${adj.id}`}
                      className="border-b hover:bg-muted/30"
                    >
                      <td className="py-2 px-3 text-sm">
                        <span className={isAddBack ? "text-green-700" : "text-red-700"}>
                          {adj.description}
                        </span>
                        <Badge
                          variant="outline"
                          className="ml-2 text-[10px] py-0 px-1"
                        >
                          {adj.adjustmentType === "add_back" ? "add" : "remove"}
                        </Badge>
                      </td>
                      {periods.map((p) => {
                        const adjsForPeriod = adjustmentsByPeriod[p] ?? [];
                        const match = adjsForPeriod.find((a) => a.id === adj.id);
                        const amount = match ? Number(match.amount) : undefined;
                        return (
                          <td
                            key={p}
                            className={`py-2 px-3 text-sm text-right ${
                              amount !== undefined
                                ? isAddBack
                                  ? "text-green-700"
                                  : "text-red-600"
                                : ""
                            }`}
                          >
                            {amount !== undefined ? formatCurrency(amount) : "\u2014"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr className="border-b">
                  <td
                    colSpan={colSpan}
                    className="py-3 px-3 text-xs text-muted-foreground text-center"
                  >
                    No EBITDA adjustments configured
                  </td>
                </tr>
              )}

              {uniqueAdjustments.length > 0 && (
                <TotalRow
                  label="Total Adjustments"
                  periods={periods}
                  valueByPeriod={adjustmentTotalByPeriod}
                  data-testid="row-total-adjustments"
                />
              )}

              {/* ============================================================ */}
              {/* SECTION 5: ADJUSTED EBITDA (prominent) */}
              {/* ============================================================ */}
              <tr
                data-testid="row-adjusted-ebitda"
                className="bg-primary/5 font-bold text-base border-t-2"
                style={{ borderTop: "2px solid #334759" }}
              >
                <td className="py-3 px-3">ADJUSTED EBITDA</td>
                {periods.map((p) => {
                  const val = adjustedEbitdaByPeriod[p];
                  return (
                    <td key={p} className="py-3 px-3 text-right">
                      {val !== undefined ? formatCurrency(val) : "\u2014"}
                    </td>
                  );
                })}
              </tr>

            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default NotionalConsolidationView;
